// What an industry item opens into. Redrawn 2026-09-20 with the list, in the same look.
//
// It had to come with the list rather than after it: both surfaces share this stylesheet and the
// portal's older classes, so restyling one alone would have left this page wearing the new tokens
// on the old layout — which reads as the old page with different colours.
//
// The order follows the list's answer to "tell me what to do": the consequence is the largest
// text on the page, the article's own headline sits under it as provenance, and every other
// consequence drawn from the same article follows. Then what to do, what has already been done,
// the article as Cobrain read it, and why it is here at all — which stays adjustable, because an
// explanation nobody can correct is decoration.
//
// The article text is corpus.signal.body, the recall text with classified spans replaced by
// markers, and never body_raw, which the app role cannot read at all.
import { ActionSelect, ActionButton, ActionRow, CardFrame, Chip, CompanyLine } from './parts/index.js';
import { useCallback, useEffect, useState } from 'react';
import { ApiError, Unauthenticated, type Store } from './api.js';
import { industry, whenWords, type DecideIndustryBody, type NewsItemView, type WhyPanel } from './industry/api.js';
import { useCompanyLines } from './company/lines.js';
import { TargetName } from './company/names.js';
import './industry.css';

function TouchedLines({ store, ids, go }: { store: string; ids: Array<string | null>; go: (path: string) => void }) {
  const lines = useCompanyLines(store, ids);
  const shown = ids.map((id) => (id ? lines[id.toLowerCase()] : undefined)).filter((l) => l !== undefined);
  return shown.length > 0 ? <div className="ind-lines">{shown.map((l) => <CompanyLine key={l.id} line={l} go={go} size="sm" />)}</div> : null;
}

type Load = { kind: 'loading' } | { kind: 'ready'; view: NewsItemView } | { kind: 'error'; message: string; status: number | null };

export function IndustryItemPage({ store, newsItemId, go }: { store: Store; newsItemId: string; go: (path: string) => void }) {
  const [load, setLoad] = useState<Load>({ kind: 'loading' });
  const [busy, setBusy] = useState(false);
  const [said, setSaid] = useState<string | null>(null);
  const [problem, setProblem] = useState<string | null>(null);
  const [raiseWith, setRaiseWith] = useState('');
  const [tick, setTick] = useState(0);
  const reload = useCallback(() => setTick((t) => t + 1), []);

  useEffect(() => {
    let cancelled = false;
    industry.item(store.tenantId, newsItemId)
      .then((view) => { if (!cancelled) setLoad({ kind: 'ready', view }); })
      .catch((e: unknown) => {
        if (cancelled) return;
        if (e instanceof Unauthenticated) { window.location.href = '/auth/login'; return; }
        setLoad({ kind: 'error', message: e instanceof Error ? e.message : String(e), status: e instanceof ApiError ? e.status : null });
      });
    return () => { cancelled = true; };
  }, [store.tenantId, newsItemId, tick]);

  const act = async (body: DecideIndustryBody) => {
    setBusy(true); setProblem(null);
    try {
      const r = await industry.decide(newsItemId, { store: store.tenantId, ...body });
      if (r.action === 'open' && r.openHref) { go(r.openHref); return; }
      const lines: string[] = [];
      if (r.landedIn) lines.push(`In ${r.landedIn.name}'s ten at ${r.landedIn.rankNo}.`);
      if (r.landingRefused) lines.push(r.landingRefused);
      if (r.toldOwner) lines.push(`${r.toldOwner.name} was told.`);
      if (r.removal) lines.push(r.removal.scope === 'everyone' ? 'Removed for everyone. You can undo it for the next hour.' : 'Hidden for you alone.');
      setSaid(lines.join(' ') || 'Recorded.');
      reload();
    } catch (e) {
      setProblem(e instanceof Error ? e.message : String(e));
    } finally { setBusy(false); }
  };

  const adjust = async (key: WhyPanel['adjustments'][number]['key']) => {
    setBusy(true); setProblem(null);
    try {
      await industry.adjust(newsItemId, store.tenantId, key);
      setSaid('Recorded as a correction. It trains the ranking. Nothing on the record changed.');
    } catch (e) {
      setProblem(e instanceof Error ? e.message : String(e));
    } finally { setBusy(false); }
  };

  if (load.kind === 'loading') {
    return (
      <div className="industry">
        <header className="ind-top"><h1>Industry</h1></header>
        <div className="ind-skeleton" aria-hidden="true"><i /><i /></div>
      </div>
    );
  }
  if (load.kind === 'error') {
    return (
      <div className="industry">
        <header className="ind-top"><h1>Industry</h1></header>
        <div className="ind-failed" role="alert">
          <p><b>{load.status === 404 ? 'That item is not on your industry page.' : 'This item could not be read.'}</b></p>
          <p>Cobrain said: {load.message}</p>
          <p>
            {load.status === 404
              ? 'Either it has aged out of the window, or it is not yours to read. Nothing stands in its place.'
              : 'Nothing was changed.'}
            {' '}
            <ActionButton reading type="button" className="ind-link" onClick={() => go('/intelligence/industry')}>Back to Industry</ActionButton>
          </p>
        </div>
      </div>
    );
  }

  const { view } = load;
  const item = view.item;
  const also = item.implications.slice(1);
  const leads = item.implications.length > 0 ? item.implication : item.title;

  return (
    <div className="industry ind-one">
      <p className="ind-back">
        <ActionButton reading type="button" onClick={() => go('/intelligence/industry')}>← Industry</ActionButton>
      </p>

      <div className={view.panels.length > 0 ? 'ind-onegrid' : 'ind-onegrid alone'}>
        <div className="ind-onemain">
          <div className="ind-meta">
            {item.regulator ? <Chip className="ind-chip reg">Regulator</Chip> : null}
            <Chip className="ind-chip">{item.publisher}</Chip>
            <Chip className="ind-chip">{view.bodyIsEmpty ? 'Headline only' : item.feedSource === 'newsletter' ? 'Daily briefing' : item.feedSource === 'rss' ? 'Feed, full text' : 'Added by hand'}</Chip>
            {item.forMe && item.forMeBecause ? <Chip className="ind-chip you">{item.forMeBecause}</Chip> : null}
            {view.why.brain ? <Chip className="ind-chip">Read by Cobrain</Chip> : null}
            <span className="ind-when">{whenWords(item.at)}</span>
          </div>

          <h1 className="ind-lead">{leads}</h1>

          {item.implications.length > 0 ? (
            <p className="ind-src one">
              {item.url
                ? <a href={item.url} target="_blank" rel="noreferrer noopener">{item.title}</a>
                : <span>{item.title}</span>}
            </p>
          ) : null}

          {item.targets.length > 0 ? (
            <div className="ind-touches">
              {item.targets.map((t, i) => <span className="ind-tch" key={i}><b><TargetName t={t} go={go} /></b> <em>{t.note}</em></span>)}
            </div>
          ) : null}
          {/* The line of each company it touches (U353): who they are to us, in the record's words. */}
          <TouchedLines store={store.tenantId} ids={item.targets.filter((t) => t.kind === 'organisation' && t.id).slice(0, 3).map((t) => t.id)} go={go} />

          {also.length > 0 ? (
            <div className="ind-also">
              <h4>And for us it also means</h4>
              <ul>{also.map((im, i) => <li key={i}><b>{im.targetName}</b> {im.text}</li>)}</ul>
            </div>
          ) : null}

          <div className="ind-opts">
            {item.owner ? (
              <ActionButton type="button" className="ind-opt lead" disabled={busy} onClick={() => void act({ action: 'raise', ownerPrincipalId: item.owner!.principalId })}>
                Raise it with {item.owner.name}
              </ActionButton>
            ) : (
              <ActionButton type="button" className="ind-opt lead" disabled={busy} onClick={() => void act({ action: 'take' })}>Take it myself</ActionButton>
            )}
            {item.owner ? <ActionButton type="button" className="ind-opt" disabled={busy} onClick={() => void act({ action: 'take' })}>Take it myself</ActionButton> : null}
            {view.panels.filter((p) => p.href).slice(0, 2).map((p) => (
              <ActionButton reading key={p.href} type="button" className="ind-opt" disabled={busy} onClick={() => go(p.href!)}>{p.linkLabel ?? `Open ${p.title}`}</ActionButton>
            ))}
          </div>

          <ActionRow variant="standing" className="ind-standing" actions={[
            { key: 'not-relevant', label: 'Not relevant', disabled: busy, onClick: () => void act({ action: 'not_relevant' }) },
            ...(view.isTrainer ? [{ key: 'not-relevant-anyone', label: 'Not relevant for anyone', disabled: busy,
              onClick: () => void act({ action: 'not_relevant', forEveryone: true }) }] : []),
          ]} />

          {view.team.length > 0 ? (
            <div className="ind-park">
              <label htmlFor="raise-with">Raise it with someone else</label>
              <ActionSelect reading id="raise-with" value={raiseWith} onChange={(e) => setRaiseWith(e.target.value)}>
                <option value="">Choose a colleague</option>
                {view.team.map((m) => <option key={m.principalId} value={m.principalId}>{m.name}</option>)}
              </ActionSelect>
              <ActionButton type="button" className="ind-opt" disabled={busy || !raiseWith} onClick={() => void act({ action: 'raise', ownerPrincipalId: raiseWith })}>Raise it</ActionButton>
            </div>
          ) : null}

          {said ? <div className="ind-said">{said}</div> : null}
          {problem ? <div className="ind-said bad" role="alert">Cobrain could not do that. {problem}</div> : null}

          <CardFrame variant="panel" className="ind-panel">
            <h2>What happened with it</h2>
            {item.taken.length === 0 && !item.removedForEveryone ? (
              <p>Nothing yet. Everyone on the team sees this same page, so when someone takes it or raises it, it says here who and when.</p>
            ) : (
              <ul className="ind-did">
                {item.taken.map((t, i) => (
                  <li key={i}>{t.action === 'take' ? `${t.byName} took it` : `${t.byName} raised it with ${t.toName ?? 'the owner'}`}, {whenWords(t.at)}</li>
                ))}
                {item.removedForEveryone ? <li>{item.removedForEveryone.byName} removed it for everyone, {whenWords(item.removedForEveryone.at)}</li> : null}
              </ul>
            )}
          </CardFrame>

          <CardFrame variant="panel" className="ind-panel">
            <h2>The article, as Cobrain read it</h2>
            <div className="ind-article">
              {view.bodyIsEmpty
                ? 'The feed carried a headline and a link only, so there is no text to show. Nothing is hidden here.'
                : view.body}
            </div>
          </CardFrame>

          <CardFrame variant="panel" className="ind-panel">
            <h2>Why is this here?</h2>
            <p>{view.why.sentence}</p>
            <ul className="ind-did">{view.why.bullets.map((b, i) => <li key={i}>{b}</li>)}</ul>
            <p className="ind-adjl">If that is wrong, say so.</p>
            <div className="ind-opts">
              {view.why.adjustments.map((a) => (
                <ActionButton key={a.key} type="button" className="ind-opt" disabled={busy} onClick={() => void adjust(a.key)}>{a.label}</ActionButton>
              ))}
            </div>
          </CardFrame>
        </div>

        {view.panels.length > 0 ? (
        <aside className="ind-side">
          {view.panels.map((p, i) => (
            <CardFrame variant="panel" className="ind-panel" key={`${p.kind}-${p.id ?? i}`}>
              <h2>{p.title}</h2>
              <p className="ind-rule">{p.rule}</p>
              {p.quote ? <p className="ind-quote"><b>In their words:</b> “{p.quote}”</p> : null}
              <dl className="ind-kv">
                {p.rows.map((r) => (<div key={r.label}><dt>{r.label}</dt><dd>{r.value}</dd></div>))}
              </dl>
              {p.href ? <p><ActionButton reading type="button" className="ind-link" onClick={() => go(p.href!)}>{p.linkLabel ?? 'Open the record'}</ActionButton></p> : null}
            </CardFrame>
          ))}
        </aside>
        ) : null}
      </div>
    </div>
  );
}
